import { X, Spinner, FileText, Sparkle } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import type { ScrapeEventMessage } from '../../../services/linkedin.service';

interface ScrapeProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  messages: ScrapeEventMessage[];
  isComplete: boolean;
  isError?: boolean;
}

export function ScrapeProgressModal({
  isOpen,
  onClose,
  title,
  messages,
  isComplete,
  isError
}: ScrapeProgressModalProps) {
  // Extract progress info from the latest progress message
  const progressMessage = messages.slice().reverse().find(m => typeof m.processed === 'number');
  const processed = progressMessage?.processed || 0;
  const total = progressMessage?.total || 0;
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
  
  // Extract the most recent log line for the status indicator
  const recentLog = messages.slice().reverse().find(m => m.type === 'log');
  const isLlmFallback = recentLog?.message?.includes('LLM calling');

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 font-sans">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="relative w-full max-w-lg bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl overflow-hidden flex flex-col border border-white/20"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-white/50">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl flex items-center justify-center ${isComplete ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600'}`}>
                  {isComplete ? <FileText size={18} weight="bold" /> : <Spinner size={18} className="animate-spin" />}
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-800">{title}</h3>
                  <p className="text-xs font-medium text-slate-500">
                    {isComplete ? 'Task finished in background' : 'Running in background'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 bg-slate-50/50 flex flex-col gap-5">
              
              {/* Status Bar */}
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-700">Overall Progress</span>
                  <span className="text-sm font-black text-slate-900">{processed} / {total}</span>
                </div>
                
                {/* Progress Bar */}
                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden relative">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${percent}%` }}
                    transition={{ ease: "easeOut", duration: 0.5 }}
                    className={`absolute top-0 left-0 h-full rounded-full ${isComplete ? 'bg-emerald-500' : isError ? 'bg-rose-500' : 'bg-blue-500'}`}
                  />
                </div>

                {/* Current Action Indicator */}
                <AnimatePresence mode="wait">
                  <motion.div 
                    key={recentLog?.message || 'idle'}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="flex items-center gap-2 mt-1"
                  >
                    {isLlmFallback ? (
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200">
                        <Sparkle size={14} weight="fill" />
                        LLM Parsing in progress...
                      </div>
                    ) : (
                      <div className="text-xs font-medium text-slate-500 truncate">
                        {recentLog?.message || (isComplete ? 'Completed.' : 'Initializing...')}
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Logs Container */}
              <div className="flex flex-col gap-2">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider pl-1">Activity Log</h4>
                <div className="bg-slate-900 rounded-2xl p-4 h-48 overflow-y-auto font-mono text-[11px] leading-relaxed shadow-inner border border-slate-800">
                  {messages.length === 0 ? (
                    <div className="text-slate-500 italic">Connecting to stream...</div>
                  ) : (
                    messages.map((msg, idx) => (
                      <motion.div 
                        initial={{ opacity: 0, x: -5 }}
                        animate={{ opacity: 1, x: 0 }}
                        key={idx} 
                        className={`flex gap-3 mb-1.5 ${
                          msg.type === 'error' ? 'text-rose-400' :
                          msg.type === 'success' ? 'text-emerald-400' :
                          msg.type === 'completed' ? 'text-emerald-300 font-bold' :
                          msg.message?.includes('LLM') ? 'text-amber-300' :
                          'text-slate-300'
                        }`}
                      >
                        <span className="text-slate-600 shrink-0 select-none">[{new Date().toLocaleTimeString([], { hour12: false, hour: '2-digit', minute:'2-digit', second:'2-digit' })}]</span>
                        <span className="break-all">
                          {msg.message || `Status: ${msg.type}`}
                        </span>
                      </motion.div>
                    ))
                  )}
                  {/* Auto-scroll anchor visually represented by the end of list */}
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-[11px] text-slate-400 font-medium">
                  You can safely close this modal. The task will continue running in the background.
                </p>
              </div>

            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
